import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { _ as _to_consumable_array } from "@swc/helpers/_/_to_consumable_array";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Core } from "@faire/design-tokens";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Input } from "@faire/web--source/slate/Input";
import { Column, Row } from "@faire/web--source/slate/Layout";
import { getSpacing, Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { TypographyStyles } from "@faire/web--source/slate/Typography";
import React, { createRef, useCallback, useMemo, useState } from "react";
import styled from "styled-components";
var DEFAULT_VALUE_LENGTH = 6;
export var VerificationInput = function(param) {
    var value = param.value, numSlots = param.numSlots, errorMessage = param.errorMessage, errorAll = param.errorAll, isValidInput = param.isValidInput, validateFn = param.validateFn, onChange = param.onChange, maxWidth = param.maxWidth, disableAutoFocus = param.disableAutoFocus, label = param.label, ariaLabel = param.ariaLabel, clearInput = param.clearInput;
    var strictLocalize = useStrictLocalization().strictLocalize;
    var numInputSlots = numSlots !== null && numSlots !== void 0 ? numSlots : DEFAULT_VALUE_LENGTH;
    var cleanedValue = _to_consumable_array(value.split("")).concat(_to_consumable_array(Array(numInputSlots).fill(""))).slice(0, numInputSlots);
    var _useState = _sliced_to_array(useState(cleanedValue), 2), code = _useState[0], setCode = _useState[1];
    var slotInputRefs = useMemo(function() {
        return Array(numInputSlots).fill(null).map(function() {
            return /*#__PURE__*/ createRef();
        });
    }, [
        numInputSlots
    ]);
    var processInput = function(e, inputIndex) {
        var inputValue = e.target.value;
        if (inputValue.length > 1) {
            return;
        }
        var newCode = _to_consumable_array(code);
        newCode[inputIndex] = inputValue;
        setCode(newCode);
        onChange === null || onChange === void 0 ? void 0 : onChange(newCode.join(""), inputValue);
        if (inputValue && inputIndex !== numInputSlots - 1) {
            focusSlot(inputIndex + 1);
        }
    };
    var handleOnPaste = function(e, slotIndex) {
        var clipboardData = e.clipboardData.getData("text").split("");
        if (slotIndex !== 0 || !clipboardData.length) {
            return;
        }
        var newCode = _to_consumable_array(clipboardData).concat(_to_consumable_array(Array(numInputSlots).fill(""))).slice(0, numInputSlots);
        setCode(newCode);
        var mergedCode = newCode.join("");
        onChange === null || onChange === void 0 ? void 0 : onChange(mergedCode, clipboardData.join(""));
    };
    var focusSlot = function(index) {
        var _slotInputRefs_index_current, _slotInputRefs_index;
        (_slotInputRefs_index = slotInputRefs[index]) === null || _slotInputRefs_index === void 0 ? void 0 : (_slotInputRefs_index_current = _slotInputRefs_index.current) === null || _slotInputRefs_index_current === void 0 ? void 0 : _slotInputRefs_index_current.focus();
    };
    var blurSlot = useCallback(function(index) {
        var _slotInputRefs_index_current, _slotInputRefs_index;
        (_slotInputRefs_index = slotInputRefs[index]) === null || _slotInputRefs_index === void 0 ? void 0 : (_slotInputRefs_index_current = _slotInputRefs_index.current) === null || _slotInputRefs_index_current === void 0 ? void 0 : _slotInputRefs_index_current.blur();
    }, [
        slotInputRefs
    ]);
    var handleBackspace = function(index) {
        return function(e) {
            var keyCode = e.keyCode;
            if (keyCode === 8 && !code[index]) {
                e.preventDefault();
                var newCode = _to_consumable_array(code);
                newCode[index - 1] = "";
                setCode(newCode);
                onChange === null || onChange === void 0 ? void 0 : onChange(newCode.join(""), "");
                focusSlot(index - 1);
            }
        };
    };
    // Clears the input field if needed
    React.useEffect(function() {
        if (clearInput && value === "") {
            setCode(Array(numInputSlots).fill(""));
            blurSlot(numInputSlots - 1);
        }
    }, [
        clearInput,
        numInputSlots,
        blurSlot,
        value
    ]);
    // Unfocuses all the input fields when a valid combined input is given
    React.useEffect(function() {
        if (isValidInput) {
            slotInputRefs.map(function(slotInput) {
                var _slotInput_current;
                return (_slotInput_current = slotInput.current) === null || _slotInput_current === void 0 ? void 0 : _slotInput_current.blur();
            });
        }
    }, [
        isValidInput,
        slotInputRefs
    ]);
    return /*#__PURE__*/ _jsxs(Column, {
        children: [
            label ? /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsx(Typography, {
                        children: label
                    }),
                    /*#__PURE__*/ _jsx(Spacer, {
                        height: 4
                    })
                ]
            }) : null,
            /*#__PURE__*/ _jsx(InputWrapper, {
                $maxWidth: maxWidth,
                children: code.map(function(slotValue, index) {
                    var _code_;
                    return /*#__PURE__*/ _jsx(SlotInput, {
                        type: "numeric",
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                        autoFocus: !disableAutoFocus && !(code === null || code === void 0 ? void 0 : (_code_ = code[0]) === null || _code_ === void 0 ? void 0 : _code_.length) && index === 0,
                        value: slotValue,
                        error: !(validateFn === null || validateFn === void 0 ? void 0 : validateFn(slotValue)) || errorAll === true,
                        onChange: function(e) {
                            return processInput(e, index);
                        },
                        onPaste: function(e) {
                            return handleOnPaste(e, index);
                        },
                        onClick: function() {
                            return focusSlot(index);
                        },
                        onKeyDown: handleBackspace(index),
                        "aria-label": ariaLabel ? "".concat(ariaLabel, " ").concat(index) : strictLocalize({
                            id: "DoNYuH",
                            defaultMessage: [
                                {
                                    type: 0,
                                    value: "Enter Slot Value "
                                },
                                {
                                    type: 1,
                                    value: "indexNumber"
                                }
                            ]
                        }, {
                            indexNumber: index
                        }),
                        "data-test-id": "input-slot-".concat(index),
                        ref: slotInputRefs[index]
                    }, index);
                })
            }),
            /*#__PURE__*/ _jsx(Spacer, {
                height: "1x"
            }),
            errorMessage ? /*#__PURE__*/ _jsx(Typography, {
                color: Core.text.critical,
                children: errorMessage
            }) : null
        ]
    });
};
var InputWrapper = styled(Row).attrs({
    gap: "2x"
}).withConfig({
    componentId: "sc-84832d89-0"
})([
    "width:100%;max-width:",
    ";"
], function(props) {
    var _props_$maxWidth;
    return (_props_$maxWidth = props.$maxWidth) !== null && _props_$maxWidth !== void 0 ? _props_$maxWidth : "340px";
});
var SlotInput = styled(Input).withConfig({
    componentId: "sc-84832d89-1"
})([
    "height:48px;padding:11px ",
    ";input{",
    " text-align:center;}"
], getSpacing("1x"), TypographyStyles({
    variant: "subheadingSansRegular"
}));
